import { useEffect, useMemo, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { BillingPlan, OnboardingPricing } from "../lib/api";
import { formatMoney, recurringLabel } from "../lib/billingPlan";
import { env } from "../env";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) stripePromise = loadStripe(env.stripePublishableKey);
  return stripePromise;
}

/**
 * Shared response shape for both `createOnboardingPaymentIntent` (authed
 * admin-invite flow) and `createPendingPaymentIntent` (deferred self-signup).
 * Both callables return the same fields — this lets PayStep stay agnostic.
 */
export interface PaymentIntentResp {
  clientSecret: string;
  paymentIntentId: string;
  status: string;
  amount: number;
  currency: string;
  couponApplied: string | null;
  activationFeeWaivedCents: number;
  /** Absent on the invite-link flow, which is always a charge. */
  checkoutMode?: "payment" | "setup";
  partnerName?: string | null;
  partnerCode?: string | null;
}

interface Props {
  pricing: OnboardingPricing;
  billingPlan: BillingPlan;
  /**
   * Called on mount to bootstrap the PaymentIntent, and again whenever the
   * user toggles a coupon. `existingPaymentIntentId` is passed on retries so
   * the backend can update the existing PI rather than creating a duplicate.
   */
  createIntent: (opts: {
    couponCode?: string;
    existingPaymentIntentId?: string;
  }) => Promise<PaymentIntentResp>;
  onPaid: (paymentIntentId: string) => void;
}

interface PaymentState {
  clientSecret: string;
  paymentIntentId: string;
  amountCents: number;
  couponApplied: string | null;
  activationFeeWaivedCents: number;
  checkoutMode: "payment" | "setup";
  partnerName: string | null;
  partnerCode: string | null;
}

function toPaymentState(resp: PaymentIntentResp): PaymentState {
  return {
    clientSecret: resp.clientSecret,
    paymentIntentId: resp.paymentIntentId,
    amountCents: resp.amount,
    couponApplied: resp.couponApplied,
    activationFeeWaivedCents: resp.activationFeeWaivedCents,
    checkoutMode: resp.checkoutMode === "setup" ? "setup" : "payment",
    partnerName: resp.partnerName ?? null,
    partnerCode: resp.partnerCode ?? null,
  };
}

export function PayStep({ pricing, billingPlan, createIntent, onPaid }: Props) {
  const [state, setState] = useState<PaymentState | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await createIntent({});
        if (cancelled) return;
        if (resp.status === "succeeded") {
          onPaid(resp.paymentIntentId);
          return;
        }
        setState(toPaymentState(resp));
      } catch (err) {
        console.error(err);
        if (!cancelled)
          setInitError(String((err as Error)?.message ?? err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createIntent, onPaid]);

  if (initError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to set up payment: {initError}
      </div>
    );
  }
  if (!state) {
    return (
      <div className="py-12 text-center text-sm text-slate-500">
        Preparing secure payment…
      </div>
    );
  }

  return (
    <Elements
      stripe={getStripePromise()}
      // Re-mount the Elements tree if the clientSecret/PI changes (coupon
      // apply triggers an `amount` update on the same PI; same clientSecret,
      // so Elements doesn't actually unmount — fine).
      key={state.clientSecret}
      options={{
        clientSecret: state.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#0b66e4",
            borderRadius: "10px",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          },
        },
      }}
    >
      <PaymentForm
        pricing={pricing}
        billingPlan={billingPlan}
        createIntent={createIntent}
        state={state}
        onStateChange={setState}
        onPaid={onPaid}
      />
    </Elements>
  );
}

interface PaymentFormProps {
  pricing: OnboardingPricing;
  billingPlan: BillingPlan;
  createIntent: (opts: {
    couponCode?: string;
    existingPaymentIntentId?: string;
  }) => Promise<PaymentIntentResp>;
  state: PaymentState;
  onStateChange: (next: PaymentState) => void;
  onPaid: (paymentIntentId: string) => void;
}

function PaymentForm({
  pricing,
  billingPlan,
  createIntent,
  state,
  onStateChange,
  onPaid,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Coupon UI state
  const [showCoupon, setShowCoupon] = useState<boolean>(
    state.couponApplied !== null,
  );
  const [couponInput, setCouponInput] = useState<string>(
    state.couponApplied ?? "",
  );
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponSuccess, setCouponSuccess] = useState<string | null>(
    state.couponApplied ? `${state.couponApplied} applied` : null,
  );

  const formatted = useMemo(
    () => ({
      total: formatMoney(state.amountCents, pricing.currency),
      firstMonth: formatMoney(pricing.firstMonthCents, pricing.currency),
      activation: formatMoney(pricing.activationFeeCents, pricing.currency),
      recurring: recurringLabel(billingPlan, pricing),
      waived: formatMoney(state.activationFeeWaivedCents, pricing.currency),
    }),
    [pricing, billingPlan, state.amountCents, state.activationFeeWaivedCents],
  );
  const cadence = billingPlan === "monthly" ? "monthly" : "weekly";

  async function applyCoupon(code: string) {
    setCouponBusy(true);
    setCouponError(null);
    setCouponSuccess(null);
    try {
      const resp = await createIntent({
        couponCode: code || undefined,
        existingPaymentIntentId: state.paymentIntentId,
      });
      const next = toPaymentState(resp);
      onStateChange(next);
      if (next.checkoutMode === "setup" && next.partnerName) {
        setCouponSuccess(null);
      } else if (code && !resp.couponApplied) {
        setCouponError("That coupon code isn't valid.");
      } else if (resp.couponApplied) {
        setCouponSuccess(`${resp.couponApplied} applied`);
      } else {
        setCouponSuccess(null);
      }
    } catch (err) {
      console.error(err);
      setCouponError(String((err as Error)?.message ?? err));
    } finally {
      setCouponBusy(false);
    }
  }

  async function handlePay(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    try {
      const code = state.partnerCode || state.couponApplied || "";
      const refreshed = toPaymentState(
        await createIntent({
          couponCode: code || undefined,
          existingPaymentIntentId: state.paymentIntentId,
        }),
      );
      const codeDropped =
        refreshed.amountCents !== state.amountCents ||
        refreshed.checkoutMode !== state.checkoutMode ||
        refreshed.couponApplied !== state.couponApplied ||
        refreshed.partnerCode !== state.partnerCode ||
        refreshed.clientSecret !== state.clientSecret;
      if (codeDropped) {
        onStateChange(refreshed);
        setError("That code is no longer valid. Review the updated total, then pay again.");
        return;
      }

      if (state.checkoutMode === "setup") {
        const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        });
        if (confirmErr) {
          setError(confirmErr.message ?? "Card setup failed");
          return;
        }
        if (setupIntent && setupIntent.status === "succeeded") {
          onPaid(setupIntent.id);
          return;
        }
        if (setupIntent && setupIntent.status === "processing") {
          onPaid(setupIntent.id);
          return;
        }
        setError(
          `Card setup is ${setupIntent?.status ?? "in an unexpected state"}. Please try again.`,
        );
        return;
      }

      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (confirmErr) {
        setError(confirmErr.message ?? "Payment failed");
        return;
      }
      if (paymentIntent && paymentIntent.status === "succeeded") {
        onPaid(paymentIntent.id);
        return;
      }
      if (paymentIntent && paymentIntent.status === "processing") {
        onPaid(paymentIntent.id);
        return;
      }
      setError(
        `Payment is ${paymentIntent?.status ?? "in an unexpected state"}. Please try again.`,
      );
    } catch (err) {
      console.error(err);
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handlePay} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Payment</h2>
        <p className="text-sm text-slate-600 mt-1">
          {state.checkoutMode === "setup"
            ? `Your card is saved for the ${cadence} charge. Nothing is charged on this card today.`
            : `Secure payment processed by Stripe. Your card is saved for the recurring ${cadence} charge.`}
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">
          Order summary
        </h3>
        <dl className="space-y-2 text-sm">
          {state.checkoutMode === "setup" ? (
            <>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-600">First month</dt>
                <dd className="text-slate-900 font-medium text-right">
                  Will be collected
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-600">One-time activation fee</dt>
                <dd className="text-slate-900 font-medium text-right">
                  Will be collected
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-slate-800">
                <span className="text-sm font-medium">
                  {state.partnerCode?.toUpperCase()} applied — activation fee and
                  first month will be collected
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setCouponInput("");
                    applyCoupon("");
                  }}
                  className="shrink-0 text-xs font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
                >
                  Remove
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <dt className="text-slate-600">First month</dt>
                <dd className="text-slate-900 font-medium">{formatted.firstMonth}</dd>
              </div>
              {state.couponApplied ? (
                <div className="flex justify-between text-emerald-700">
                  <dt>One-time activation fee (waived)</dt>
                  <dd className="font-medium line-through opacity-70">
                    {formatted.activation}
                  </dd>
                </div>
              ) : (
                <div className="flex justify-between">
                  <dt className="text-slate-600">One-time activation fee</dt>
                  <dd className="text-slate-900 font-medium">
                    {formatted.activation}
                  </dd>
                </div>
              )}

              {state.couponApplied ? (
                <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800">
                  <span className="text-sm font-medium">
                    {state.couponApplied} applied — {formatted.waived} activation
                    waived
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setCouponInput("");
                      applyCoupon("");
                    }}
                    className="text-xs font-medium text-emerald-900 underline underline-offset-2 hover:text-emerald-700"
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </>
          )}

          <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
            <dt className="font-semibold text-slate-900">Due today</dt>
            <dd className="font-semibold text-slate-900">{formatted.total}</dd>
          </div>
          <div className="text-xs text-slate-500 pt-3">
            After {pricing.trialDays} days, {formatted.recurring} will be
            charged automatically to the same card.
          </div>
        </dl>

        {state.checkoutMode !== "setup" && !state.couponApplied ? (
          <div className="mt-4 border-t border-slate-200 pt-3">
            {!showCoupon ? (
              <button
                type="button"
                onClick={() => setShowCoupon(true)}
                className="text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                Have a coupon code?
              </button>
            ) : (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Coupon code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    autoCapitalize="characters"
                    autoComplete="off"
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-base font-mono uppercase tracking-wide focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => applyCoupon(couponInput.trim())}
                    disabled={couponBusy || !couponInput.trim()}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {couponBusy ? "Checking…" : "Apply"}
                  </button>
                </div>
                {couponError ? (
                  <p className="mt-1 text-xs text-red-600">{couponError}</p>
                ) : couponSuccess ? (
                  <p className="mt-1 text-xs text-emerald-700">{couponSuccess}</p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <PaymentElement
          options={{
            layout: "tabs",
            wallets: { applePay: "auto", googlePay: "auto" },
          }}
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={submitting || !stripe || !elements}
        className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-600 transition"
        data-payment-intent-id={state.paymentIntentId}
      >
        {submitting
          ? "Processing…"
          : state.checkoutMode === "setup"
            ? "Save card and finish enrollment"
            : `Pay ${formatted.total} now`}
      </button>
    </form>
  );
}